import React, { useState, useMemo } from 'react';
import { Input, Menu, Typography, Space, Tag, Empty, Card, Button, Layout } from 'antd';
import {
  SearchOutlined,
  BookOutlined,
  RobotOutlined,
  FileTextOutlined,
  InboxOutlined,
  CodeOutlined,
  ToolOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { HELP_CONTENT, searchHelpArticles } from '../data/helpContent';
import '../styles/accessibility.css';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Header, Content, Sider } = Layout;

/**
 * HelpPage Component
 * Location: frontend/src/pages/HelpPage.js
 *
 * Standalone full-page help center (opens in new window from account menu).
 * Replaces the previous modal-based help center.
 */
const HelpPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Icon mapping for categories
  const categoryIcons = {
    'getting-started': <BookOutlined />,
    'ai-coach': <RobotOutlined />,
    'report-writer': <FileTextOutlined />,
    'archive': <InboxOutlined />,
    'keyboard-shortcuts': <CodeOutlined />,
    'troubleshooting': <ToolOutlined />,
  };

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchHelpArticles(searchQuery);
  }, [searchQuery]);

  // Get current category and article
  const currentCategory = HELP_CONTENT[selectedCategory];
  const currentArticle = selectedArticle
    ? currentCategory?.articles.find((a) => a.id === selectedArticle)
    : null;

  // Handle category selection
  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  // Handle article selection
  const handleArticleSelect = (articleId) => {
    setSelectedArticle(articleId);
    setSearchQuery('');
  };

  // Handle search result selection
  const handleSearchResultSelect = (result) => {
    setSelectedCategory(result.categoryKey);
    setSelectedArticle(result.id);
    setSearchQuery('');
  };

  // Render category list view
  const renderCategoryList = () => {
    if (searchQuery && searchResults.length > 0) {
      return (
        <div style={{ padding: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>
            Search Results ({searchResults.length})
          </Title>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {searchResults.map((result) => (
              <Card
                key={`${result.categoryKey}-${result.id}`}
                hoverable
                onClick={() => handleSearchResultSelect(result)}
                style={{ cursor: 'pointer' }}
                bodyStyle={{ padding: '16px' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{result.title}</Text>
                    <Tag color="blue" style={{ marginLeft: '8px', fontSize: '11px' }}>
                      {result.categoryTitle}
                    </Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {result.description}
                  </Text>
                  <div>
                    {result.tags.map((tag) => (
                      <Tag key={tag} style={{ fontSize: '11px' }}>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Card>
            ))}
          </Space>
        </div>
      );
    }

    if (searchQuery && searchResults.length === 0) {
      return (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <Empty
            description={
              <span>
                No results found for "<strong>{searchQuery}</strong>"
              </span>
            }
          />
        </div>
      );
    }

    return (
      <div style={{ padding: '24px' }}>
        <Title level={4} style={{ marginBottom: '16px' }}>
          {currentCategory.title}
        </Title>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {currentCategory.articles.map((article) => (
            <Card
              key={article.id}
              hoverable
              onClick={() => handleArticleSelect(article.id)}
              style={{ cursor: 'pointer' }}
              bodyStyle={{ padding: '16px' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>{article.title}</Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {article.description}
                </Text>
                <div>
                  {article.tags.map((tag) => (
                    <Tag key={tag} style={{ fontSize: '11px' }}>
                      {tag}
                    </Tag>
                  ))}
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                    {article.estimatedReadTime}
                  </Text>
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      </div>
    );
  };

  // Render article view
  const renderArticle = () => {
    if (!currentArticle) return null;

    return (
      <div style={{ padding: '32px' }}>
        <Space
          direction="vertical"
          size="small"
          style={{ width: '100%', marginBottom: '24px' }}
        >
          <Button
            onClick={() => setSelectedArticle(null)}
            style={{
              padding: '4px 0',
              height: 'auto',
              border: 'none',
              background: 'none',
              color: 'var(--rlc-text-accent)',
            }}
          >
            ← Back to {currentCategory.title}
          </Button>
          <Title level={2} style={{ margin: '8px 0' }}>
            {currentArticle.title}
          </Title>
          <div>
            {currentArticle.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
            <Text type="secondary" style={{ marginLeft: '8px' }}>
              {currentArticle.estimatedReadTime} read
            </Text>
          </div>
        </Space>

        <div
          className="help-article-content"
          style={{
            fontSize: '15px',
            lineHeight: '1.7',
          }}
        >
          <ReactMarkdown>{currentArticle.content}</ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          background: 'var(--rlc-surface)',
          padding: '0 24px',
          borderBottom: '1px solid var(--rlc-surface-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          <BookOutlined style={{ fontSize: '24px', color: 'var(--rlc-state-info)' }} />
          <Title level={3} style={{ margin: 0 }}>
            Help Center
          </Title>
        </Space>
        <Button
          icon={<CloseOutlined />}
          onClick={() => window.close()}
          type="text"
        >
          Close
        </Button>
      </Header>

      <Layout>
        <Sider
          width={280}
          style={{
            background: 'var(--rlc-surface)',
            borderRight: '1px solid var(--rlc-surface-muted)',
          }}
        >
          <div style={{ padding: '16px' }}>
            <Search
              placeholder="Search help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              aria-label="Search help articles"
            />
          </div>

          <Menu
            mode="inline"
            selectedKeys={[selectedCategory]}
            style={{ border: 'none' }}
            onSelect={({ key }) => handleCategorySelect(key)}
          >
            {Object.entries(HELP_CONTENT).map(([key, category]) => (
              <Menu.Item key={key} icon={categoryIcons[key]}>
                {category.title}
              </Menu.Item>
            ))}
          </Menu>
        </Sider>

        <Content
          style={{
            background: 'var(--rlc-surface)',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          {selectedArticle ? renderArticle() : renderCategoryList()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default HelpPage;
